import type { PropsWithChildren } from "react";
import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { NOT_AVAILABLE } from "app/filters/const";
import { abbrNumberFilter } from "app/filters/ngFilters";
import ManagementCard from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/ManagementCard";
import ManagementCardListItem from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/ManagementCardListItem";
import ManagementCardTextBody from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/ManagementCardTextBody";
import {
    StyledManagementCardContentList,
    StyledManagementCardFooterWrapper,
    StyledManagementCardTagWrapper,
} from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/styles";
import IntegrationButton from "app/pages/sales-intelligence/modules/manage-integrations/components/integration-button/IntegrationButton";
import {
    MANAGE_INTEGRATIONS_CARD_CONFIGURE,
    MANAGE_INTEGRATIONS_CARD_CONFIGURED,
    MANAGE_INTEGRATIONS_CARD_MANAGE,
    MY_INTEGRATIONS_FIND_COMPANIES_WEBHOOKS_EMPTY_TEXT,
    WEBHOOKS_FIND_COMPANIES_NEW_COMPANIES,
    WEBHOOKS_FIND_COMPANIES_TITLE,
    WEBHOOKS_FIND_COMPANIES_TRACKED_SEARCHES,
    WEBHOOKS_LAST_DELIVERY,
} from "app/pages/sales-intelligence/modules/manage-integrations/constants/translations";
import type { IFindNewCompaniesData } from "app/pages/sales-intelligence/modules/manage-integrations/hooks/useFindNewCompaniesData";
import openExternalLink from "app/pages/sales-intelligence/modules/manage-integrations/utils/openExternalLink";
import { salesforceFormatDate } from "app/pages/sales-intelligence/modules/manage-integrations/utils/salesforceFormatDate";
import { FIND_NEW_COMPANIES_API_GUIDE_URL } from "app/pages/sales-intelligence/modules/management/sub-modules/constants";
import {
    MANAGEMENT_COMMON_DAYS,
    MANAGEMENT_COMMON_HOURS,
    MANAGEMENT_COMMON_MINUTES,
} from "app/pages/sales-intelligence/pages/management/translations/managementKeys";
import manageIntegrationsTrackingService from "app/pages/sales-intelligence/modules/manage-integrations/services/tracking";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";

const formatCount = abbrNumberFilter();

export const FindNewCompaniesCard = ({
    webhooks,
    isConfigured,
    isEnabled,
    hasAccess,
    children,
}: PropsWithChildren<IFindNewCompaniesData>): React.ReactElement | null => {
    const { translate } = useTranslate();

    const lastDeliveryText = useMemo(() => {
        const latestNotified = (webhooks ?? []).reduce<string | null>((latest, webhook) => {
            if (!webhook.lastNotifiedDate) {
                return latest;
            }

            const ts = Date.parse(`${webhook.lastNotifiedDate}Z`);

            if (Number.isNaN(ts)) {
                return latest;
            }

            if (!latest) {
                return webhook.lastNotifiedDate;
            }

            return ts > Date.parse(`${latest}Z`) ? webhook.lastNotifiedDate : latest;
        }, null);

        return latestNotified
            ? salesforceFormatDate(
                  `${latestNotified}Z`,
                  translate(MANAGEMENT_COMMON_MINUTES),
                  translate(MANAGEMENT_COMMON_HOURS),
                  translate(MANAGEMENT_COMMON_DAYS),
              )
            : NOT_AVAILABLE;
    }, [webhooks]);

    const trackedSearchesCount = new Set(webhooks?.map((w) => w.queryId)).size;
    const newCompaniesCount = webhooks?.reduce((sum, w) => sum + w.notifiedCount, 0) ?? 0;

    const trackedSearchesText = trackedSearchesCount > 0 ? String(trackedSearchesCount) : NOT_AVAILABLE;
    const newCompaniesText = newCompaniesCount > 0 ? formatCount(newCompaniesCount) : NOT_AVAILABLE;

    const handleToGatewayPage = () => {
        void siNavigator.goToGatewayPage("findNewCompaniesWebhook");
    };

    const handleGuideClick = () => {
        manageIntegrationsTrackingService.trackFindNewCompaniesWebhookGuideClick();
        openExternalLink(FIND_NEW_COMPANIES_API_GUIDE_URL);
    };

    const handleManageClick = () => {
        manageIntegrationsTrackingService.trackFindNewCompaniesWebhookClick();

        if (!isConfigured) {
            handleToGatewayPage();
            return;
        }

        void siNavigator.goToManagementSavedSearchWebhooksPage();
    };

    const cardFooter = (
        <StyledManagementCardFooterWrapper>
            <StyledManagementCardTagWrapper>{children}</StyledManagementCardTagWrapper>

            <IntegrationButton
                dataAutomation="find-new-companies-button"
                featureName="findNewCompaniesWebhook"
                mainButtonType={isConfigured ? "outlined" : "primary"}
                hasAccess={hasAccess}
                isEnabled={isEnabled}
                onManageClick={handleManageClick}
                onUpgradeClick={handleToGatewayPage}
            >
                {translate(isConfigured ? MANAGE_INTEGRATIONS_CARD_MANAGE : MANAGE_INTEGRATIONS_CARD_CONFIGURE)}
            </IntegrationButton>
        </StyledManagementCardFooterWrapper>
    );

    const cardBody = isConfigured ? (
        <StyledManagementCardContentList>
            <ManagementCardListItem title={translate(WEBHOOKS_FIND_COMPANIES_TRACKED_SEARCHES)}>
                {trackedSearchesText}
            </ManagementCardListItem>
            <ManagementCardListItem title={translate(WEBHOOKS_FIND_COMPANIES_NEW_COMPANIES)}>
                {newCompaniesText}
            </ManagementCardListItem>
            <ManagementCardListItem title={translate(WEBHOOKS_LAST_DELIVERY)}>
                {lastDeliveryText}
            </ManagementCardListItem>
        </StyledManagementCardContentList>
    ) : (
        <ManagementCardTextBody>{translate(MY_INTEGRATIONS_FIND_COMPANIES_WEBHOOKS_EMPTY_TEXT)}</ManagementCardTextBody>
    );

    return (
        <ManagementCard
            dataAutomation="find-new-companies-card"
            title={translate(WEBHOOKS_FIND_COMPANIES_TITLE)}
            chipText={isConfigured ? translate(MANAGE_INTEGRATIONS_CARD_CONFIGURED) : undefined}
            chipType="success"
            onHeaderActionClick={!isConfigured ? handleGuideClick : undefined}
            footer={cardFooter}
        >
            {cardBody}
        </ManagementCard>
    );
};
